import type { LearnerSearchResultItem } from '../../types';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { AutocompleteField, IconButton, type IconNames, ListBox, Body } from '@workramp/ui';
import ReactHtmlParser from 'react-html-parser';
import { AppState } from '~/common/explicit-exports';
import { useDebouncedValue, useMediaQuery } from '~/hooks';
import { MixPanel } from '~/common';
import { getIconProps, openSearchResult, goToAdvancedSearch } from '../../utils';
import { ALGOLIA_SEARCH_DEBOUNCE_MS } from '../../constants';
import { useLearnerSearchResults } from '../../api';
import { getLearnerSearchResultItemFromAlgoliaHit } from './getLearnerSearchResultItemFromAlgoliaHit';
import { AutocompleteFieldContainer, LearnerSearchItemStyledStack } from './LearnerSearchBar.style';

enum LearnerSearchBarMixPanelEvents {
  LEARNER_SEARCH_BAR_FOCUS = 'learner_search_bar_focus',
  LEARNER_SEARCH_BAR_CLICKS_ADVANCED_SEARCH = 'learner_search_bar_clicks_advanced_search',
  LEARNER_SEARCH_BAR_CLICKS_RESULT = 'learner_search_bar_clicks_result',
}

const contains = (textVal: string, inputVal: string) => textVal.includes(inputVal);

const { I18n } = window;

type ResultItem = Pick<LearnerSearchResultItem, 'id' | 'resultTitle'>;

export const LearnerSearchBar = () => {
  const enableAdvancedSearch = !AppState.isWhiteLabel;

  const [value, setValue] = useState('');
  const [enterToAdvandedSearch, setEnterToAdvandedSearch] = useState(enableAdvancedSearch);
  const [isFocused, setIsFocused] = useState(false);
  const TRANSLATED_SEARCH_TRAINING = I18n.t('js.learner.search.search_training');
  const TRANSLATED_ADVANCED_SEARCH = I18n.t('js.learner.search.advanced_search_tooltip');
  const NO_RESULTS_FOUND: ResultItem = {
    id: 'no-result-found',
    resultTitle: I18n.t('js.common.item_picker.no_results'),
  };
  const SEARCHING: ResultItem = {
    id: 'searching',
    resultTitle: I18n.t('js.common.item_picker.loading'),
  };
  const { colors, breakpoints } = useTheme();
  const isSmAndUp = useMediaQuery(breakpoints.up('sm'));

  const debouncedSearchTerm = useDebouncedValue(value, ALGOLIA_SEARCH_DEBOUNCE_MS);
  const learnerSearchResult = useLearnerSearchResults({
    query: debouncedSearchTerm,
  });

  let items: LearnerSearchResultItem[] | ResultItem[];

  if (!debouncedSearchTerm) {
    items = [];
  } else if (learnerSearchResult.isLoading) {
    items = [SEARCHING];
  } else if (learnerSearchResult.data?.pages.length > 0 && learnerSearchResult.data?.pages[0].total > 0) {
    items = learnerSearchResult.data.pages
      .map((page) => page.data)
      .flat()
      .map(getLearnerSearchResultItemFromAlgoliaHit);
  } else {
    // NOTE: currently, this would be the path if algoliaSearchResult.isError is true
    // do we want to handle this differently?
    items = [NO_RESULTS_FOUND];
  }

  return (
    <AutocompleteFieldContainer $isFocused={isFocused}>
      <AutocompleteField
        defaultFilter={contains}
        inputValue={value}
        items={items}
        disabledKeys={[NO_RESULTS_FOUND.id, SEARCHING.id]}
        label={null}
        aria-label="Search"
        showTrailingButton
        leadingIconName="search:regular"
        data-testid="learner-search-autocomplete"
        trailingButtonEl={
          enableAdvancedSearch ? (
            <IconButton
              label={TRANSLATED_ADVANCED_SEARCH}
              data-testid="advanced-search-button"
              iconName="controls-preferences:regular"
              variant="text"
              color="secondary"
              onPressStart={() => {
                MixPanel.track(LearnerSearchBarMixPanelEvents.LEARNER_SEARCH_BAR_CLICKS_ADVANCED_SEARCH, {
                  searchQuery: value,
                });
                goToAdvancedSearch(value);
              }}
            />
          ) : undefined
        }
        onFocus={() => {
          setIsFocused(true);
          setEnterToAdvandedSearch(enableAdvancedSearch);
          MixPanel.track(LearnerSearchBarMixPanelEvents.LEARNER_SEARCH_BAR_FOCUS);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
        placeholder={TRANSLATED_SEARCH_TRAINING}
        onInputChange={(v) => {
          setEnterToAdvandedSearch(enableAdvancedSearch);
          setValue(v);
        }}
        onSelectionChange={(key) => {
          const selection = items.find(({ id }) => id === key) as LearnerSearchResultItem;
          if (selection) {
            MixPanel.track(LearnerSearchBarMixPanelEvents.LEARNER_SEARCH_BAR_CLICKS_RESULT, {
              title: selection.resultTitle,
              type: selection.type,
              id: selection.id,
            });
            openSearchResult(selection);
          }
        }}
        allowsCustomValue
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value && enterToAdvandedSearch) {
            goToAdvancedSearch(value);
          }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            setEnterToAdvandedSearch(false);
          }
        }}
      >
        {(item) => {
          if (item.id === NO_RESULTS_FOUND.id) {
            return <ListBox.Item key={item.id}>{item.resultTitle}</ListBox.Item>;
          }
          const { contentSource, id, lastUpdated, resultTitle, resultSubtitle, type, textValue } =
            item as LearnerSearchResultItem;
          const { name: iconName, color: iconColor } = getIconProps(type as IconNames, contentSource, colors);

          return (
            <ListBox.Item
              key={id}
              iconName={iconName}
              iconColor={iconColor}
              suffix={isSmAndUp ? `${lastUpdated ? lastUpdated.replaceAll(' ', '\u00A0') : ''}` : null}
              textValue={textValue}
              alignItem="start"
            >
              <LearnerSearchItemStyledStack size={0}>
                <Body level="md" maxLines={1}>
                  {ReactHtmlParser(resultTitle)}
                </Body>

                <Body level="sm" color={colors.on[600]} maxLines={1}>
                  {ReactHtmlParser(resultSubtitle)}
                </Body>
                {!isSmAndUp && (
                  <Body level="sm" color={colors.on[600]} maxLines={1}>
                    {lastUpdated ? lastUpdated.replaceAll(' ', '\u00A0') : ''}
                  </Body>
                )}
              </LearnerSearchItemStyledStack>
            </ListBox.Item>
          );
        }}
      </AutocompleteField>
    </AutocompleteFieldContainer>
  );
};
